import { useState, useEffect } from 'react';
import ConnectionDetailShell from './ConnectionDetailShell';
import ConnectionDetailPage from './ConnectionDetailPage';
import { COLORS, DETAIL_TOKENS } from '../../styles/UISystem';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSession } from '../../contexts/SessionContext';
import { useConnectionContext, useConnectionData } from '../../contexts/ConnectionStateContext';
import AboutSection from '../UISystem/Sections/AboutSection';
import PhotosSection, { hasPhotoMessages } from '../UISystem/Sections/PhotosSection';
import NicknameField from '../UISystem/Forms/NicknameField';
import useConnectionNote from '../../hooks/useConnectionNote';
import { ButtonRounded } from '../UISystem/Buttons';
import { ALL_CONTACT_KEYS } from '../../utils/contactTypeConfig';
import {
  gateVenueMetadata,
  deriveMetPlaceParts,
  metPreposition,
  formatMetPlace,
  formatDateTimeForDisplay,
} from '../../utils/locationFormatters';
import { isFullSender } from '../../utils/contactType';
import PhotoLightbox from '../UISystem/Chat/PhotoLightbox';
import ConnectionAvatar from '../UISystem/ConnectionAvatar';
import { IconCircleButton } from '../UISystem/Icons/IconCircleButton';
import { isSansChatVariant } from '../../utils/exchangeVariant';
import ContactFields from '../UISystem/Forms/ContactFields';
import { PaperPlaneTilt } from '@phosphor-icons/react/dist/ssr/PaperPlaneTilt';
import { isIOS } from '../../utils/platformUtils';
import IosHandoffSheet from '../Overlay/IosHandoffSheet';
import { DownloadSimple } from '@phosphor-icons/react/dist/ssr/DownloadSimple';

/**
 * AnonConnectionDetailPage - Detailed connection view for anonymous users
 *
 * Rebuilt to the 2026-08-18 Detail frames, which converge this page and the
 * Profile-side page on ONE layout with different permissions:
 * avatar → name → Note → bare "Met at" line → photos → Lives in → About →
 * contact rows → Save to contacts.
 *
 * Two changes from the 2026-07-18 friendly-card mock this replaces: the icon
 * row is gone (the contact ROWS carry the channels now — still live links, just
 * not editable), and the WE MET card loses its container to read as a plain
 * line. Everything an Anon sees here is Profile-shared, so every row is locked.
 */

const AnonConnectionDetailPage = () => {
  const { navigateTo, goBack } = useNavigation();
  // One read per render — the variant can't change mid-load (resolve-once)
  const sansChat = isSansChatVariant();
  const { sessionChatRoomId } = useSession();
  const { setCurrentChatRoomId, updateNickname } = useConnectionContext();
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // iOS misdirects on the vCard preview exactly as it does on the .ics: the
  // bright checkmark reads as "saved" and saves nothing. The signpost used to
  // live only on TEXT's flow card, so every other variant — THREAD included —
  // reached this page's save with no warning at all. Gated here, at the one
  // handler both entry points share (the sans-chat download icon and the
  // "Save contact" button).
  const [saveSheetOpen, setSaveSheetOpen] = useState(false);

  const connectionData = useConnectionData(sessionChatRoomId);
  // ONE note per connection (2026-08-18 Detail frames). This side already wrote
  // its note to connection_views.nickname, so there's usually nothing to drain —
  // the hook still runs for anons carrying a note on the legacy column.
  const noteText = useConnectionNote(sessionChatRoomId, connectionData, false);

  // WE MET reads connectionMetadata (single, venue-bearing) rather than the meetings
  // table: the anon's seeded meeting rows often lack the venue in location_data and
  // duplicate on re-seed — an exchange-flow issue, separate from this page.

  // Sync currentChatRoomId in ConnectionStateContext
  useEffect(() => {
    if (sessionChatRoomId) {
      setCurrentChatRoomId(sessionChatRoomId);
    }
  }, [sessionChatRoomId, setCurrentChatRoomId]);

  const handleBack = () => {
    goBack('anon-connections-list');
  };

  const handleChatClick = () => {
    navigateTo('anon-connection-page', { source: 'connection-detail' });
  };

  return (
    <ConnectionDetailPage
      chatRoomId={sessionChatRoomId}
      isProfileUser={false} // Anon user viewing profile data
    >
      {({ userData, connectionMetadata, onAddToContacts, chatMessages }) => {
        const sharedFields = userData?.sharedFields || [];

        // The icon row carries the channels — icons ARE live links (no CONTACTS
        // value rows on this page anymore).
        const sharedContacts = {};
        for (const field of ALL_CONTACT_KEYS) {
          if (sharedFields.includes(field) && userData?.[field]) {
            sharedContacts[field] = userData[field];
          }
        }

        const requestSaveToPhone = () => {
          if (isIOS()) {
            setSaveSheetOpen(true);
            return;
          }
          onAddToContacts();
        };

        return (
          <>
            <ConnectionDetailShell
              onBack={handleBack}
              topRight={
                sansChat ? (
                  // Sans-chat variants have no chat door, so the corner the chat
                  // icon isn't using carries save-contact instead (mock 5a's
                  // dark download icon, moved out of the retired icon row).
                  userData ? (
                    <IconCircleButton
                      icon={DownloadSimple}
                      size={22}
                      buttonSize={40}
                      onClick={requestSaveToPhone}
                      backgroundColor="#555555"
                      iconColor="#FFFFFF"
                    />
                  ) : null
                ) : (
                  <IconCircleButton
                    icon={PaperPlaneTilt}
                    size={22}
                    buttonSize={40}
                    onClick={handleChatClick}
                    iconColor="#555555"
                  />
                )
              }
            >
              {/* Profile Content Block - at top of card */}
              <div
                style={{
                  paddingTop: '5px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                }}
              >
                {/* Profile Photo — ring only for FULL hosts (user ruling
                    2026-08-08). senderAccountStatus rides the card payload;
                    MISSING = legacy host = Full, so old cards keep the ring */}
                <ConnectionAvatar
                  photoUrl={userData?.photoUrl}
                  name={userData?.name}
                  isRoyaleUser={isFullSender(userData)}
                  placeholderBg={COLORS.LIGHT_GRAY}
                  initialColor={COLORS.MEDIUM_GRAY}
                />

                {/* Name */}
                <div
                  style={{
                    marginTop: '12px',
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#000000',
                    letterSpacing: '-0.3px',
                    textAlign: 'center',
                  }}
                >
                  {userData?.name || ''}
                </div>
              </div>

              {/* Main content area — single flex gap drives the spacing between cards */}
              <div
                style={{
                  flex: 1,
                  padding: '20px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '20px',
                }}
              >
                {/* NOTE — the one note field (Note + Nickname merged), directly
                    under the name per the updated frames (user, 2026-08-18).
                    Viewer-private, DB-first on connection_views.nickname. */}
                <div style={{ width: '100%', maxWidth: DETAIL_TOKENS.SECTION_MAX_WIDTH }}>
                  <NicknameField
                    value={noteText}
                    onSave={(text) => updateNickname(sessionChatRoomId, text)}
                  />
                </div>

                {/* MET — a bare line, no container (frames). The preposition
                    rides metPreposition rather than a hand-rolled "Met at": the
                    2026-08-08 leak was exactly a hand-rolled copy of this
                    sentence, which bypassed the venue consent gate. Both the
                    label and the renderer read the GATED clone, so an
                    unconfirmed host venue still shows the neighbourhood. */}
                {connectionMetadata &&
                  (() => {
                    const gated = gateVenueMetadata(connectionMetadata);
                    const { primaryPlace } = deriveMetPlaceParts(gated);
                    const place = formatMetPlace(connectionMetadata);
                    if (!place) return null;
                    // "Met at" for a venue, "Met in" for a neighbourhood — the
                    // preposition rides metPreposition rather than a hand-rolled
                    // copy (the 2026-08-08 leak was exactly that, and it bypassed
                    // the venue consent gate).
                    const metLabel = `Met ${metPreposition(primaryPlace, gated)}`;
                    const when = connectionMetadata.meetingDate
                      ? formatDateTimeForDisplay(
                          connectionMetadata.meetingDate,
                          connectionMetadata.meetingUtcOffset
                        )
                      : '';
                    return (
                      <div
                        style={{
                          width: '100%',
                          maxWidth: DETAIL_TOKENS.SECTION_MAX_WIDTH,
                          textAlign: 'center',
                        }}
                      >
                        <div style={{ fontSize: '13px', color: '#8E8E93', marginBottom: '2px' }}>
                          {metLabel}
                        </div>
                        {/* No pin, no street address — the address can't be
                            edited anywhere and is often wrong in dense cities
                            (user ruling 2026-08-19). The location stays stored. */}
                        <div style={{ fontSize: '17px', color: '#000000' }}>{place}</div>
                        {when && (
                          <div style={{ fontSize: '15px', color: '#8E8E93', marginTop: '2px' }}>
                            {when}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                {/* PHOTOS — bare grid, no container (frames) */}
                {hasPhotoMessages(chatMessages) && (
                  <div style={{ width: '100%', maxWidth: DETAIL_TOKENS.SECTION_MAX_WIDTH }}>
                    <PhotosSection messages={chatMessages} onOpen={setLightboxUrl} headerless />
                  </div>
                )}

                {/* ABOUT — one card: where they live, role/company, what
                    they're up to, places they like. This page used to carry its
                    own inline copy of these two cards, including the " @ "
                    between role and company; both are retired in favour of the
                    shared component, so the two detail pages cannot drift
                    again (user, 2026-08-20). */}
                <AboutSection userData={userData} />

                {/* CONTACTS — every row locked: everything an Anon sees here is
                    Profile-shared, so nothing on this page is theirs to edit.
                    Icons still open the linked app and the phone row still texts
                    (showLinks), which is what the retired icon row carried. */}
                {Object.keys(sharedContacts).length > 0 && (
                  <div style={{ width: '100%', maxWidth: DETAIL_TOKENS.SECTION_MAX_WIDTH }}>
                    <ContactFields
                      userData={sharedContacts}
                      disabled={true}
                      showLinks={true}
                      compact={true}
                      starterFields={[]}
                      onChange={() => {}}
                    />
                  </div>
                )}

                {/* Save to contacts — below the contact rows (frames). The old
                    "everything above this button gets saved" divider semantic is
                    dropped; it was never surfaced to users. Black CTA until
                    tapped, then quiet secondary for the rare re-save — we can
                    only detect the tap (it hands off to the iOS contact card),
                    not whether it was actually saved. Sans-chat carries this
                    action in the corner instead. */}
                {!sansChat && userData && (
                  <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <ButtonRounded
                      text="Save to contacts"
                      icon={DownloadSimple}
                      onClick={requestSaveToPhone}
                      size="compact"
                      variant={connectionData?.hasAddedToContacts ? 'secondary' : 'primary'}
                      style={{ width: DETAIL_TOKENS.ROW_MAX_WIDTH }}
                    />
                  </div>
                )}

                {/* Bottom padding - ensures content is scrollable past safe areas */}
                <div style={{ height: '20px', flexShrink: 0 }} />
              </div>
            </ConnectionDetailShell>

            {/* Photo lightbox */}
            {lightboxUrl && (
              <PhotoLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
            )}

            {/* iOS signpost before the vCard preview */}
            {saveSheetOpen && (
              <IosHandoffSheet
                headline="On the next screen, scroll to"
                rows={['Create New Contact', 'Add to Existing Contact']}
                onOk={() => {
                  setSaveSheetOpen(false);
                  onAddToContacts();
                }}
                onClose={() => setSaveSheetOpen(false)}
              />
            )}
          </>
        );
      }}
    </ConnectionDetailPage>
  );
};

export default AnonConnectionDetailPage;
